// Filtro por cliente no Pipeline de Negócios.
//
// Pedido do Lucas (30/09/2026): *"no pipeline quero poder filtrar por cliente"*.
// A tela já tinha filtro de status, de vendedor e de origem; o cliente só
// existia dentro da busca por texto, que casa título, área e observações ao
// mesmo tempo — digitar "Nova" trazia negócio de outra empresa cujo título
// tivesse "nova".
//
// Medido em produção em 30/09/2026 (somente leitura), e é o que dita o desenho:
//
//  • 479 negócios em **186 clientes** distintos — mas o cadastro tem **330**
//    clientes ativos. A lista do filtro é só de quem TEM negócio: 144 opções
//    que nunca filtram nada só fazem o dropdown rolar.
//  • **1 negócio sem empresa**. Ele ganha opção própria ("(sem empresa)"), e só
//    quando existe — opção que nunca acha nada é pior que opção nenhuma.
//  • **2 clientes com negócio estão FORA da lista `/comercial/clientes`** (a
//    rota devolve só `ativo = true` e `tipo_cadastro = 'comercial'`). Por isso o
//    rótulo tem degrau: sem o cadastro, vale a razão social que vem no próprio
//    negócio. Sem isso, dois clientes reais apareceriam como "Cliente #123".
//  • Razão social REPETIDA entre clientes com negócio: hoje zero. O CNPJ entra
//    no rótulo **só quando o nome visível se repete** — que é o caso Consplan
//    (um CNPJ por empreendimento, mesma razão social; Lucas, 31/08/2026). Nos
//    outros 186 o CNPJ em toda linha só encurtaria o nome na tela. Ele fica
//    sempre na BUSCA e sempre no `title`, então nada se perde.
//
// A régua é pura de propósito — o teste roda no CI (job dre-frontend), onde o
// jest do CRA5 não consegue carregar o antd v6.

/** O que a régua precisa de um negócio. O resto da linha não interessa. */
export type NegocioCliente = {
  cliente_id?: number | null;
  cliente_nome?: string | null;
};

/** O que a régua precisa do cadastro de clientes (a rota devolve `cl.*`). */
export type ClienteCadastro = {
  id: number;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
};

export type OpcaoCliente = {
  /** `String(cliente_id)` ou `SEM_EMPRESA`. */
  value: string;
  /** O que aparece no CAMPO FECHADO (nome + contagem). */
  label: string;
  /** Só o nome — o dropdown desenha nome e contagem separados, para a contagem
   *  nunca ser comida pelo "…" (8 dos 187 nomes passam da largura). */
  nome: string;
  /** Tooltip: nome completo + CNPJ, sempre. */
  title: string;
  /** Alvo da busca por NOME, já em minúsculas (razão social + fantasia). */
  busca: string;
  /** Só os dígitos do CNPJ. Campo SEPARADO de propósito — ver `buscaCasaCliente`. */
  cnpjDigitos: string;
  /** Quantos negócios este cliente tem na lista carregada. */
  negocios: number;
};

/** Valor do filtro para os negócios que não têm empresa no cadastro. */
export const SEM_EMPRESA = 'sem-empresa';

const soDigitos = (v: unknown) => String(v == null ? '' : v).replace(/\D/g, '');

/**
 * Compara nome de empresa como gente compara: sem acento, sem caixa, sem
 * pontuação e sem a forma societária no fim.
 */
const FORMA_SOCIETARIA = /(^| )(ltda|ltda me|me|epp|eireli|sa|s a|spe|cia|mei|ss)( |$)/g;
export function chaveDeNome(v: unknown): string {
  return String(v == null ? '' : v)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(FORMA_SOCIETARIA, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Nome visível do cliente: a fantasia só entra quando diz um nome DIFERENTE.
 *
 * Medido na tela em 30/09/2026: "Brair Empreendimentos Imobiliários (BRAIR
 * EMPREENDIMENTOS IMOBILIARIOS LTDA)" estourava a largura do dropdown e a
 * contagem de negócios sumia no "…" — repetindo o MESMO nome duas vezes. O
 * parêntese vale para "Companhia Riograndense de Saneamento Corsan (CORSAN EST
 * UNIF)", que é como as pessoas chamam a empresa; não para um eco da razão
 * social com outra caixa, outro acento ou sem o "LTDA".
 */
export function nomeVisivel(c: ClienteCadastro | null, nomeDoNegocio?: string | null): string {
  const razao = String((c && c.razao_social) || nomeDoNegocio || '').trim();
  const fantasia = String((c && c.nome_fantasia) || '').trim();
  if (!razao) return fantasia;
  if (!fantasia) return razao;
  const kr = chaveDeNome(razao);
  const kf = chaveDeNome(fantasia);
  if (!kf || kr === kf || kr.includes(kf) || kf.includes(kr)) return razao;
  return `${razao} (${fantasia})`;
}

/**
 * As opções do filtro: um cliente por negócio existente, em ordem alfabética,
 * com a contagem de negócios no rótulo. "(sem empresa)" por último, e só se
 * houver negócio sem empresa.
 */
export function opcoesClienteDoPipeline(
  negocios: NegocioCliente[], clientes: ClienteCadastro[],
): OpcaoCliente[] {
  const cadastro = new Map<number, ClienteCadastro>();
  for (const c of clientes || []) if (c && c.id != null) cadastro.set(Number(c.id), c);

  // Conta por cliente E guarda a razão social que veio no negócio — é o degrau
  // para os clientes que não estão na lista do cadastro.
  const porCliente = new Map<number, { n: number; nomeDoNegocio: string }>();
  let semEmpresa = 0;
  for (const p of negocios || []) {
    if (p.cliente_id == null) { semEmpresa++; continue; }
    const id = Number(p.cliente_id);
    const atual = porCliente.get(id);
    if (atual) atual.n++;
    else porCliente.set(id, { n: 1, nomeDoNegocio: String(p.cliente_nome || '').trim() });
  }

  const cru = Array.from(porCliente.entries()).map(([id, info]) => {
    const c = cadastro.get(id) || null;
    const visivel = nomeVisivel(c, info.nomeDoNegocio) || `Cliente #${id}`;
    const cnpj = (c && c.cnpj) || '';
    return { id, visivel, cnpj, negocios: info.n, cadastro: c };
  });

  // O CNPJ só entra no rótulo quando o nome visível NÃO distingue: é o caso
  // Consplan. Distinguir com CNPJ quem já é único deixaria as 186 linhas longas
  // sem resolver nada.
  const repetidos = new Set<string>();
  const vistos = new Set<string>();
  for (const x of cru) {
    const k = x.visivel.toLowerCase();
    if (vistos.has(k)) repetidos.add(k); else vistos.add(k);
  }

  const opcoes: OpcaoCliente[] = cru.map(x => {
    const ambiguo = repetidos.has(x.visivel.toLowerCase());
    const comCnpj = x.visivel + (ambiguo ? (x.cnpj ? ` · ${x.cnpj}` : ' · sem CNPJ') : '');
    return {
      value: String(x.id),
      label: `${comCnpj} · ${x.negocios}`,
      nome: comCnpj,
      title: x.visivel + (x.cnpj ? ` · ${x.cnpj}` : ' · sem CNPJ'),
      busca: [x.visivel, (x.cadastro && x.cadastro.nome_fantasia) || '']
        .filter(Boolean).join(' ').toLowerCase(),
      cnpjDigitos: soDigitos(x.cnpj),
      negocios: x.negocios,
    };
  }).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

  if (semEmpresa > 0) {
    opcoes.push({
      value: SEM_EMPRESA,
      label: `(sem empresa) · ${semEmpresa}`,
      nome: '(sem empresa)',
      title: 'Negócios sem empresa vinculada no cadastro',
      busca: 'sem empresa',
      cnpjDigitos: '',
      negocios: semEmpresa,
    });
  }
  return opcoes;
}

/** O negócio passa pelo filtro de cliente? Filtro vazio = passa tudo. */
export function casaFiltroCliente(p: NegocioCliente, filtro: string): boolean {
  if (!filtro) return true;
  if (filtro === SEM_EMPRESA) return p.cliente_id == null;
  return p.cliente_id != null && String(p.cliente_id) === filtro;
}

/**
 * A busca por escrita do dropdown (regra da casa: todo dropdown filtra pelo que
 * se escreve). Casa nome e fantasia por pedaço, e o CNPJ por dígitos — com ou
 * sem máscara, a partir de 3 dígitos.
 *
 * 🚨 O CNPJ mora num campo SEPARADO do nome. No `SelectCliente` os dígitos do
 * CNPJ estão no MESMO texto do nome, e por isso digitar "9" casa toda empresa
 * que tenha 9 em algum lugar do CNPJ — o piso de 3 dígitos não protege nada
 * (`includes` já casou antes). Aqui o piso vale de verdade; nome com número
 * ("Grupo 3 Engenharia") continua casando pelo nome, que é o certo.
 */
export function buscaCasaCliente(
  input: string, opcao: { busca?: string; cnpjDigitos?: string },
): boolean {
  const q = String(input || '').toLowerCase().trim();
  if (!q) return true;
  if (String((opcao && opcao.busca) || '').includes(q)) return true;
  const digitos = q.replace(/\D/g, '');
  return digitos.length >= 3 && String((opcao && opcao.cnpjDigitos) || '').includes(digitos);
}
